/**
 * @module Cart
 */

import {ClientApplication} from '../../client';
import {actionWrapper, isValidOptionalNumber, isValidOptionalString, ActionSet} from '../helper';
import {Group, MetaAction} from '../types';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
} from '../Error';
import {
  Action,
  ActionType,
  Address,
  AddCustomerAddressPayload,
  AddLineItemPayload,
  Customer,
  Data,
  DiscountAmount,
  DiscountCode,
  LineItem,
  NoteAttributes,
  Options,
  Payload,
  Properties,
  RemoveLineItemDiscountPayload,
  RemoveLineItemPayload,
  RemoveLineItemPropertiesPayload,
  RemovePropertiesPayload,
  SetCustomerPayload,
  SetDiscountPayload,
  SetLineItemDiscountPayload,
  SetLineItemPropertiesPayload,
  SetPropertiesPayload,
  UpdateCustomerAddressPayload,
  UpdateLineItemPayload,
} from './types';

/**
 * Validation
 */

function isValidAddress(address: Address) {
  return (
    isValidOptionalString(address.address1) &&
    isValidOptionalString(address.address2) &&
    isValidOptionalString(address.city) &&
    isValidOptionalString(address.company) &&
    isValidOptionalString(address.firstName) &&
    isValidOptionalString(address.lastName) &&
    isValidOptionalString(address.phone) &&
    isValidOptionalString(address.provice) &&
    isValidOptionalString(address.country) &&
    isValidOptionalString(address.zip) &&
    isValidOptionalString(address.name) &&
    isValidOptionalString(address.proviceCode) &&
    isValidOptionalString(address.countryCode)
  );
}

function isValidAddresses(addresses?: Address[]) {
  if (!addresses) {
    return true;
  }

  return Array.isArray(addresses) && addresses.every(isValidAddress);
}

function isValidCart(cart: Data) {
  return (
    (cart.cartDiscount === undefined || isValidDiscount(cart.cartDiscount)) &&
    (cart.customer === undefined ||
      isValidCustomer(cart.customer) ||
      isValidAddresses(cart.customer.addresses)) &&
    isValidOptionalString(cart.grandTotal) &&
    (cart.lineItems === undefined || isValidLineItems(cart.lineItems)) &&
    isValidNoteAttributes(cart.noteAttributes) &&
    isValidOptionalString(cart.subTotal) &&
    isValidOptionalString(cart.taxTotal)
  );
}

function isValidCustomer(customer: Customer) {
  return (
    isValidOptionalNumber(customer.id) &&
    isValidOptionalString(customer.email) &&
    isValidOptionalString(customer.firstName) &&
    isValidOptionalString(customer.lastName) &&
    isValidOptionalString(customer.note)
  );
}

function isValidDiscount(discount: DiscountAmount | DiscountCode) {
  return (
    isValidOptionalNumber((discount as DiscountAmount).amount) &&
    isValidOptionalString((discount as DiscountAmount).discountDescription) &&
    isValidOptionalString((discount as DiscountAmount).type) &&
    isValidOptionalString((discount as DiscountCode).discountCode)
  );
}

function isValidLineItemDiscount(discount: DiscountAmount) {
  return (
    isValidOptionalNumber(discount.amount) &&
    isValidOptionalString(discount.discountDescription) &&
    isValidOptionalString(discount.type)
  );
}

function isValidLineItem(lineItem: LineItem) {
  return (
    isValidOptionalNumber(lineItem.price) &&
    typeof lineItem.quantity === 'number' &&
    isValidOptionalString(lineItem.title) &&
    isValidOptionalNumber(lineItem.variantId)
  );
}

function isValidLineItems(lineItems?: LineItem[]) {
  if (!lineItems) {
    return true;
  }

  return Array.isArray(lineItems) && lineItems.every(isValidLineItem);
}

function isValidNoteAttributes(noteAttributes?: NoteAttributes) {
  return (
    noteAttributes === undefined ||
    (Array.isArray(noteAttributes) &&
      noteAttributes.every(note => typeof note.name === 'string' && typeof note.value === 'string'))
  );
}

function isValidProperties(properties: Properties) {
  return (
    typeof properties === 'object' &&
    !Array.isArray(properties) &&
    Object.keys(properties).every(key => typeof properties[key] === 'string')
  );
}

function isValidPropertiesKey(keys: string[]) {
  return Array.isArray(keys) && keys.every(key => typeof key === 'string');
}

export function validationError(action: MetaAction): undefined | ErrorAction {
  if (!action.payload || typeof action.payload.id !== 'string') {
    return invalidPayload(action, 'Missing payload id');
  }
  let payload;
  switch (action.type) {
    case ActionType.FETCH:
      return undefined;
    case ActionType.UPDATE:
      payload = action.payload as Payload;

      return !payload.data || !isValidCart(payload.data)
        ? invalidPayload(action, 'Invalid update payload')
        : undefined;
    case ActionType.SET_CUSTOMER:
      payload = action.payload as SetCustomerPayload;

      return !payload.data || !isValidCustomer(payload.data)
        ? invalidPayload(action, 'Invalid set customer payload')
        : undefined;
    case ActionType.REMOVE_CUSTOMER:
      return undefined;
    case ActionType.ADD_CUSTOMER_ADDRESS:
      payload = action.payload as AddCustomerAddressPayload;

      return !payload.data || !isValidAddress(payload.data)
        ? invalidPayload(action, 'Invalid add customer address payload')
        : undefined;
    case ActionType.UPDATE_CUSTOMER_ADDRESS:
      payload = action.payload as UpdateCustomerAddressPayload;

      return !payload.data || typeof payload.index !== 'number' || !isValidAddress(payload.data)
        ? invalidPayload(action, 'Invalid update customer address payload')
        : undefined;

    case ActionType.SET_DISCOUNT:
      payload = action.payload as SetDiscountPayload;

      return !payload.data || !isValidDiscount(payload.data)
        ? invalidPayload(action, 'Invalid set discount payload')
        : undefined;
    case ActionType.REMOVE_DISCOUNT:
      return undefined;
    case ActionType.SET_PROPERTIES:
      payload = action.payload as SetPropertiesPayload;

      return !payload.data || !isValidProperties(payload.data)
        ? invalidPayload(action, 'Invalid add properties payload')
        : undefined;
    case ActionType.REMOVE_PROPERTIES:
      payload = action.payload as RemovePropertiesPayload;

      return !payload.data || !isValidPropertiesKey(payload.data)
        ? invalidPayload(action, 'Invalid remove properties payload')
        : undefined;
    case ActionType.CLEAR:
      return undefined;
    case ActionType.ADD_LINE_ITEM:
      payload = action.payload as AddLineItemPayload;

      return !payload.data || !isValidLineItem(payload.data)
        ? invalidPayload(action, 'Invalid add line item payload')
        : undefined;
    case ActionType.UPDATE_LINE_ITEM:
      payload = action.payload as UpdateLineItemPayload;

      return !payload.data ||
        typeof payload.index !== 'number' ||
        typeof payload.data.quantity !== 'number'
        ? invalidPayload(action, 'Invalid update line item payload')
        : undefined;
    case ActionType.REMOVE_LINE_ITEM:
      payload = action.payload as RemoveLineItemPayload;

      return typeof payload.index !== 'number'
        ? invalidPayload(action, 'Invalid remove line item payload')
        : undefined;
    case ActionType.SET_LINE_ITEM_DISCOUNT:
      payload = action.payload as SetLineItemDiscountPayload;

      return !payload.data ||
        typeof payload.index !== 'number' ||
        !isValidLineItemDiscount(payload.data)
        ? invalidPayload(action, 'Invalid set line item discount payload')
        : undefined;
    case ActionType.REMOVE_LINE_ITEM_DISCOUNT:
      payload = action.payload as RemoveLineItemDiscountPayload;

      return typeof payload.index !== 'number'
        ? invalidPayload(action, 'Invalid remove line item discount payload')
        : undefined;
    case ActionType.SET_LINE_ITEM_PROPERTIES:
      payload = action.payload as SetLineItemPropertiesPayload;

      return !payload.data || typeof payload.index !== 'number' || !isValidProperties(payload.data)
        ? invalidPayload(action, 'Invalid add line item properties payload')
        : undefined;
    case ActionType.REMOVE_LINE_ITEM_PROPERTIES:
      payload = action.payload as RemoveLineItemPropertiesPayload;

      return !payload.data ||
        typeof payload.index !== 'number' ||
        !isValidPropertiesKey(payload.data)
        ? invalidPayload(action, 'Invalid remove line item properties payload')
        : undefined;
    default:
      return invalidActionType(action);
  }
}

/**
 * Cart
 */

export class Cart extends ActionSet {
  constructor(app: ClientApplication<any>, options?: Options) {
    super(app, Group.Cart, Group.Cart, options ? options.id : undefined);
  }

  dispatch(
    action: Action.FETCH | Action.REMOVE_CUSTOMER | Action.REMOVE_DISCOUNT | Action.CLEAR,
  ): Cart;
  dispatch(action: Action.UPDATE, payload: Payload): Cart;
  dispatch(action: Action.SET_CUSTOMER, payload: SetCustomerPayload): Cart;
  dispatch(action: Action.ADD_CUSTOMER_ADDRESS, payload: AddCustomerAddressPayload): Cart;
  dispatch(action: Action.UPDATE_CUSTOMER_ADDRESS, payload: UpdateCustomerAddressPayload): Cart;
  dispatch(action: Action.SET_DISCOUNT, payload: SetDiscountPayload): Cart;
  dispatch(action: Action.SET_PROPERTIES, payload: SetPropertiesPayload): Cart;
  dispatch(action: Action.REMOVE_PROPERTIES, payload: RemovePropertiesPayload): Cart;
  dispatch(action: Action.ADD_LINE_ITEM, payload: AddLineItemPayload): Cart;
  dispatch(action: Action.UPDATE_LINE_ITEM, payload: UpdateLineItemPayload): Cart;
  dispatch(action: Action.REMOVE_LINE_ITEM, payload: RemoveLineItemPayload): Cart;
  dispatch(action: Action.SET_LINE_ITEM_DISCOUNT, payload: SetLineItemDiscountPayload): Cart;
  dispatch(action: Action.REMOVE_LINE_ITEM_DISCOUNT, payload: RemoveLineItemDiscountPayload): Cart;
  dispatch(action: Action.SET_LINE_ITEM_PROPERTIES, payload: SetLineItemPropertiesPayload): Cart;
  dispatch(
    action: Action.REMOVE_LINE_ITEM_PROPERTIES,
    payload: RemoveLineItemPropertiesPayload,
  ): Cart;
  dispatch(action: Action, payload?: any) {
    switch (action) {
      case Action.FETCH:
        this.dispatchCartAction(ActionType.FETCH);
        break;
      case Action.UPDATE:
        this.dispatchCartAction(ActionType.UPDATE, payload);
        break;
      case Action.SET_CUSTOMER:
        this.dispatchCartAction(ActionType.SET_CUSTOMER, payload);
        break;
      case Action.REMOVE_CUSTOMER:
        this.dispatchCartAction(ActionType.REMOVE_CUSTOMER, payload);
        break;
      case Action.ADD_CUSTOMER_ADDRESS:
        this.dispatchCartAction(ActionType.ADD_CUSTOMER_ADDRESS, payload);
        break;
      case Action.UPDATE_CUSTOMER_ADDRESS:
        this.dispatchCartAction(ActionType.UPDATE_CUSTOMER_ADDRESS, payload);
        break;
      case Action.SET_DISCOUNT:
        this.dispatchCartAction(ActionType.SET_DISCOUNT, payload);
        break;
      case Action.REMOVE_DISCOUNT:
        this.dispatchCartAction(ActionType.REMOVE_DISCOUNT, payload);
        break;
      case Action.SET_PROPERTIES:
        this.dispatchCartAction(ActionType.SET_PROPERTIES, payload);
        break;
      case Action.REMOVE_PROPERTIES:
        this.dispatchCartAction(ActionType.REMOVE_PROPERTIES, payload);
        break;
      case Action.CLEAR:
        this.dispatchCartAction(ActionType.CLEAR, payload);
        break;
      case Action.ADD_LINE_ITEM:
        this.dispatchCartAction(ActionType.ADD_LINE_ITEM, payload);
        break;
      case Action.UPDATE_LINE_ITEM:
        this.dispatchCartAction(ActionType.UPDATE_LINE_ITEM, payload);
        break;
      case Action.REMOVE_LINE_ITEM:
        this.dispatchCartAction(ActionType.REMOVE_LINE_ITEM, payload);
        break;
      case Action.SET_LINE_ITEM_DISCOUNT:
        this.dispatchCartAction(ActionType.SET_LINE_ITEM_DISCOUNT, payload);
        break;
      case Action.REMOVE_LINE_ITEM_DISCOUNT:
        this.dispatchCartAction(ActionType.REMOVE_LINE_ITEM_DISCOUNT, payload);
        break;
      case Action.SET_LINE_ITEM_PROPERTIES:
        this.dispatchCartAction(ActionType.SET_LINE_ITEM_PROPERTIES, payload);
        break;
      case Action.REMOVE_LINE_ITEM_PROPERTIES:
        this.dispatchCartAction(ActionType.REMOVE_LINE_ITEM_PROPERTIES, payload);
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }

  private dispatchCartAction(type: ActionType, payload?: any) {
    this.app.dispatch(
      actionWrapper({
        type,
        group: Group.Cart,
        payload: {
          ...(payload || {}),
          id: this.id,
        },
      }),
    );
  }
}

export function create(app: ClientApplication<any>, options?: Options) {
  return new Cart(app, options);
}



// WEBPACK FOOTER //
// ./src/actions/Cart/actions.ts